import { create } from "zustand";
import { persist } from "zustand/middleware";
import type {
  FiatTransactionHistory,
  HistoryTransaction,
  SendTransactionHistory,
  SwapTransactionHistory,
  TxTypeToTransactionMap,
} from "../../core/types/history";
import { HistoryTxType } from "../../core/types/history";
import { compareTransactionIds, getHistoryTransactionId } from "../../services";

interface V0HistoryStoreState {
  // in v0 store, all transactions are Swap transactions
  transactions: SwapTransactionHistory[];
}

interface HistoryStoreState {
  // in v1 store, transactions have different types
  transactions: HistoryTransaction[];
}

export type ReplaceTransactionStatusParams =
  | ({
      txType: HistoryTxType.SWAP;
    } & Pick<
      SwapTransactionHistory,
      "statusResponse" | "status" | "transactionId"
    >)
  | ({
      txType: HistoryTxType.SEND;
    } & Pick<SendTransactionHistory, "hash" | "status">)
  | ({
      txType: HistoryTxType.BUY;
    } & Pick<FiatTransactionHistory, "status" | "transactionHash" | "orderId">);

interface HistoryStore extends HistoryStoreState {
  persistTransaction: (tx: HistoryTransaction) => void;
  replaceTransactionStatus: ({}: ReplaceTransactionStatusParams) => void;
  removeTransaction: ({}: {
    txType: HistoryTxType;
    transactionId: string;
  }) => void;
  replaceTransactionAtNonce: ({}: {
    nonce: number;
    fromAddress: string;
    newTx: HistoryTransaction;
  }) => void;
  findTransaction: <T extends HistoryTxType>({}: {
    txType: T;
    transactionId: string | undefined;
  }) => TxTypeToTransactionMap[T] | undefined;
}

export const useHistoryStore = create(
  persist<
    HistoryStore,
    [],
    [],
    // persisted state only includes properties, not methods
    HistoryStoreState
  >(
    (set, get) => ({
      transactions: [],
      persistTransaction: (tx) => {
        set((state) => {
          return {
            transactions: [...state.transactions, tx],
          };
        });
      },
      replaceTransactionStatus: (params) => {
        switch (params.txType) {
          case HistoryTxType.SWAP: {
            set((state) => ({
              transactions: state.transactions.map((tx) => {
                if (tx.txType !== HistoryTxType.SWAP) return tx;

                // TODO: do we need this comparison?
                return compareTransactionIds(
                  params.transactionId,
                  tx.data.transactionId
                )
                  ? ({
                      ...tx,
                      data: {
                        ...tx.data,
                        statusResponse: params.statusResponse,
                        status: params.status,
                      },
                    } satisfies HistoryTransaction)
                  : tx;
              }),
            }));

            break;
          }
          case HistoryTxType.BUY: {
            set((state) => ({
              transactions: state.transactions.map((tx) => {
                if (
                  tx.txType !== HistoryTxType.BUY ||
                  tx.data.orderId !== params.orderId
                ) {
                  return tx;
                }

                return {
                  ...tx,
                  data: {
                    ...tx.data,
                    status: params.status,
                    transactionHash: params.transactionHash,
                  },
                } satisfies HistoryTransaction;
              }),
            }));
            break;
          }
          case HistoryTxType.SEND: {
            set((state) => ({
              transactions: state.transactions.map((tx) => {
                if (
                  tx.txType !== HistoryTxType.SEND ||
                  tx.data.hash !== params.hash
                ) {
                  return tx;
                }

                return {
                  ...tx,
                  data: {
                    ...tx.data,
                    status: params.status,
                  },
                } satisfies HistoryTransaction;
              }),
            }));
            break;
          }
        }
      },
      removeTransaction: ({ transactionId, txType }) => {
        {
          set((state) => ({
            transactions: state.transactions.filter((tx) => {
              if (tx.txType !== txType) return true;

              return getHistoryTransactionId(tx) !== transactionId;
            }),
          }));
        }
      },
      replaceTransactionAtNonce: ({ fromAddress, newTx, nonce }) => {
        if (newTx.txType === HistoryTxType.SWAP) {
          set((state) => {
            const index = state.transactions.findIndex(
              (tx) =>
                tx.txType === HistoryTxType.SWAP &&
                tx.data.nonce === nonce &&
                tx.data.fromAddress === fromAddress
            );
            if (index !== -1) {
              const newTransactions = [...state.transactions];
              newTransactions[index] = newTx;
              return { transactions: newTransactions };
            }
            return state;
          });
        }
      },
      findTransaction: <T extends HistoryTxType>({
        transactionId,
        txType,
      }: {
        txType: T;
        transactionId: string | undefined;
      }): TxTypeToTransactionMap[T] | undefined => {
        const { transactions } = get();

        return transactions.find((tx): tx is TxTypeToTransactionMap[T] => {
          if (tx.txType !== txType) return false;

          return getHistoryTransactionId(tx) === transactionId;
        });
      },
    }),
    {
      // ".v2" here references to Squid V2, not the store version
      // This name should not be changed to avoid breaking changes
      name: "squid.history.store.v2",
      // store version, defaults to 0
      version: 1,
      // migrate stores on previous versions
      migrate(persistedState, version) {
        if (version === 0) {
          const v0State = persistedState as V0HistoryStoreState;

          return {
            transactions: v0State.transactions.map((v0Tx) => ({
              txType: HistoryTxType.SWAP,
              data: v0Tx,
            })),
          };
        }

        return persistedState as HistoryStoreState;
      },
    }
  )
);
